import {useContext, useMemo} from 'react';
import {useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {ErrorMessage} from '../../../components/ErrorMessage/ErrorMessage';
import {Loading} from '../../../components/Loading/Loading';
import {CompanyContext} from '../../../contexts/CompanyContext/CompanyContext';
import {SnackBarContext} from '../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {CompanyProfileBenefits} from '../../../features/company/profile/components/CompanyProfileBenefits';
import {CompanyProfileGeneralInformation} from '../../../features/company/profile/components/CompanyProfileGeneralInformation';
import {CompanyProfileHeader} from '../../../features/company/profile/components/CompanyProfileHeader';
import {CompanyProfileLocations} from '../../../features/company/profile/components/CompanyProfileLocations';
import {CompanyProfileMedia} from '../../../features/company/profile/components/CompanyProfileMedia';
import {CompanyBenefitsService} from '../../../features/company/profile/services/CompanyBenefitsService';
import {CompanyLocationsService} from '../../../features/company/profile/services/CompanyLocationsService';
import {CompanyProfileService} from '../../../features/company/profile/services/CompanyProfileService';
import {CompanyProfile} from '../../../features/company/profile/types';
import {Walkthrough, WalkthroughTrigger} from '../../../features/walkthroughs';
import {companyProfileWalkthrough} from '../../../features/walkthroughs/steps/companyProfile';
import {empionGreen} from '../../../layouts/theme/EmpionColors';
import {CompanyAdminRoleData} from '../../../types/User';

export default function CompanyProfilePage() {
  const {t} = useTranslation();
  const {company: companyCache, locations, benefits} = useContext(CompanyContext);
  const [company, setCompany] = useState<CompanyProfile | undefined>();
  const [companyHeaderImgUrl, setCompanyHeaderImgUrl] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const {currentUser} = useContext(UserContext);
  const {dispatchMessage} = useContext(SnackBarContext);

  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  const companyProfileService = useMemo(() => new CompanyProfileService(), []);
  const companyBenefitsService = useMemo(() => new CompanyBenefitsService(), []);
  const companyLocationsService = useMemo(() => new CompanyLocationsService(), []);

  const fetchCompany = async () => {
    try {
      const response = await companyProfileService.get(companyId);
      const companyData = response.data;
      setCompany(companyData);
      setCompanyHeaderImgUrl(companyData?.headerImageUrl);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch company profile:', error);
      setError('Failed to fetch company profile');
      setLoading(false);
    }
  };

  const updateCompany = async (data: CompanyProfile) => {
    try {
      setCompany(data);
      setCompanyHeaderImgUrl(data.headerImageUrl);
      await companyProfileService.patch(data);
      const message = t('shared.formSubmit.success');
      const severity = 'success';
      companyCache.invalidateCache();
      dispatchMessage(message, severity);
    } catch (error: any) {
      const message = t('shared.formSubmit.error');
      const severity = 'error';
      dispatchMessage(message, severity);
    }
  };

  const updateCompanyLogo = async (logoId: string | null) => {
    try {
      const response = await companyProfileService.updateLogo(companyId, logoId);
      setCompany(response.data);
      const message = t('shared.formSubmit.success');
      const severity = 'success';
      companyCache.invalidateCache();
      dispatchMessage(message, severity);
    } catch (error: any) {
      const message = t('shared.formSubmit.error');
      const severity = 'error';
      dispatchMessage(message, severity);
    }
  };

  const updateBenefits = async (data: CompanyProfile['benefits']) => {
    if (!company) {
      return;
    }

    try {
      const result = await companyBenefitsService.updateCompanyBenefits(company?.resourceId, data);
      const message = t('shared.formSubmit.success');
      const severity = 'success';
      setCompany({...company, benefits: result.benefits});
      benefits.invalidateCache();
      dispatchMessage(message, severity);
    } catch (error: any) {
      const message = t('shared.formSubmit.error');
      const severity = 'error';
      dispatchMessage(message, severity);
    }
  };

  const createBenefits = async (data: CompanyProfile['benefits']) => {
    if (!company) {
      return;
    }
    try {
      const result = await companyBenefitsService.createCompanyBenefits(company?.resourceId, data);
      const message = t('shared.formSubmit.success');
      const severity = 'success';

      setCompany({...company, benefits: result.benefits});
      benefits.invalidateCache();
      dispatchMessage(message, severity);
    } catch (error: any) {
      const message = t('shared.formSubmit.error');
      const severity = 'error';
      dispatchMessage(message, severity);
    }
  };

  // This function handles the updating and/or inserting of company locations
  const upsertLocations = async (data: CompanyProfile['locations']) => {
    if (!company) return;

    const existingLocations = data.filter((loc) => loc.resourceId && loc.resourceId !== '');
    const newLocations = data.filter((loc) => !loc.resourceId);

    try {
      let added: {locations: CompanyProfile['locations']} = {locations: []};
      let updated: {locations: CompanyProfile['locations']} = {
        locations: [],
      };

      if (existingLocations.length > 0) {
        added = await companyLocationsService.updateCompanyLocations(company.resourceId, existingLocations);
      }

      if (newLocations.length > 0) {
        updated = await companyLocationsService.createCompanyLocations(company.resourceId, newLocations);
      }

      const message = t('shared.formSubmit.success');
      const severity = 'success';

      setCompany({
        ...company,
        locations: [...added.locations, ...updated.locations],
      });
      locations.invalidateCache();
      dispatchMessage(message, severity);
    } catch (err) {
      const message = t('shared.formSubmit.error');
      const severity = 'error';
      dispatchMessage(message, severity);
    }
  };

  const deleteLocations = async (locationIds: string[]) => {
    if (!company) return;

    try {
      await companyLocationsService.deleteCompanyLocations(company?.resourceId, locationIds);
      locations.invalidateCache();
    } catch (error) {
      console.error('Error deleting data:', error);
    }
  };

  useEffect(() => {
    fetchCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          minHeight: '70vh',
        }}
      >
        <Loading />
      </Box>
    );
  }

  if (error || !company) {
    return <ErrorMessage />;
  }

  return (
    <Box sx={{maxWidth: 1120, mx: 'auto', width: '100%'}}>
      <Typography sx={{fontSize: '14px', lineHeight: 1.5, color: empionGreen[700], mb: 0.5}}>
        {t('companyProfile.page.breadcrumb')}
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3}}>
        <Typography
          component='h1'
          data-tour='profile.title'
          sx={{fontSize: '34px', lineHeight: 1.5, fontWeight: 700, color: empionGreen[700]}}
        >
          {t('companyProfile.page.title')}
        </Typography>
        <WalkthroughTrigger walkthroughId='companyProfile' />
      </Box>

      <CompanyProfileHeader
        company={company}
        updateCompany={updateCompany}
        updateCompanyLogo={updateCompanyLogo}
        companyHeaderImgUrl={companyHeaderImgUrl}
      />

      <CompanyProfileGeneralInformation company={company} updateCompany={updateCompany} />
      <CompanyProfileLocations company={company} upsertLocations={upsertLocations} deleteLocations={deleteLocations} />
      <CompanyProfileBenefits company={company} updateBenefits={updateBenefits} createBenefits={createBenefits} />
      <CompanyProfileMedia company={company} updateCompany={updateCompany} />

      <Walkthrough definition={companyProfileWalkthrough} enabled={!loading && !!company} />
    </Box>
  );
}
